import { useApolloClient, useMutation } from '@apollo/client';
import { ApiErrorCode } from '@orn/global/errors/apiErrorCodes.js';
import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';
import { usePlaidLink, type PlaidLinkOnSuccessMetadata, type PlaidLinkOptions } from 'react-plaid-link';

import {
  PlaidLinkTokenItemOutcome,
  PlaidLinkTokenStatus,
  type LinkTokenIntent,
  type PlaidLinkTokenReplaceRefusal,
} from '../../__generated__/globalTypes';
import { useFinancialAccounts } from '../../hooks/queries/useFinancialAccounts';
import { useFeatureGate } from '../../hooks/useFeatureGates';
import {
  incrementPeopleProperty,
  trackAccountConnected,
  trackPlaidLinkExited,
  trackPlaidLinkOpened,
} from '../../lib/mixpanel';
import { evictBankConnectionReads, evictHouseholdNetWorth, evictWorkItemReads } from '../../providers/apolloCache';
import { extractErrorCode } from '../../utils/apollo/error';

import { PLAID_LINK_TOKEN, PLAID_LINK_TOKEN_UPDATED } from './plaidLinkToken';
import { CREATE_FINANCIAL_ACCOUNTS, CREATE_LINK_TOKEN, ARCHIVE_BANK_CONNECTION_WORK_ITEMS } from './plaidMutations';

import type {
  PlaidLinkTokenFieldsFragment,
  PlaidLinkTokenQuery,
  PlaidLinkTokenQueryVariables,
  PlaidLinkTokenUpdatedSubscription,
} from './__generated__/plaidLinkToken.gql';
import type {
  CreateFinancialAccountsMutation,
  CreateFinancialAccountsMutationVariables,
  CreateLinkTokenMutation,
  CreateLinkTokenMutationVariables,
} from './__generated__/plaidMutations.gql';

/** The financial accounts a successful link produced (new-connection or reconnection both return this shape). */
export type LinkedFinancialAccounts = CreateFinancialAccountsMutation['createFinancialAccounts'];

interface ILinkSessionWait {
  session: PlaidLinkTokenFieldsFragment | null;
  /** The wait ended on a failing read, so the outcome is unknown rather than absent. */
  pollFailed: boolean;
}

const LINK_SESSION_POLL_MS = 2_000;
// A multi-item session's outcome arrives over Plaid's webhook, not `onSuccess`;
// past this the flow reports it as still pending rather than keep spinning.
const LINK_SESSION_WAIT_MS = 90_000;
// An exit may follow banks the user did link before closing; a short look
// catches them without stalling a plain cancel behind a full wait.
const LINK_SESSION_EXIT_WAIT_MS = 8_000;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export interface IUseAccountLinkOptions {
  /**
   * Skip the connection-selection modal and go straight to Plaid for an
   * existing connection (reconnection). `null` / a string both mean "skip the
   * modal"; `undefined` (the default) means "open the modal first". Matches the
   * `PlaidLinkButton` prop semantics.
   */
  bankConnectionId?: string | null;
  /**
   * What the session against `bankConnectionId` is for. `AddInvestments` asks
   * Plaid for its account picker; the default repairs the login and shows none.
   * Ignored without a connection.
   */
  intent?: LinkTokenIntent;
  /**
   * A held connection this session replaces with a fresh link to its
   * institution: the new Item takes over the connection's accounts and
   * history. Skips the modal like a reconnection, but opens a new-bank session
   * (no `bankConnectionId`, no `intent`) whose outcome arrives over the webhook
   * as `REPLACED`. Ignored when `bankConnectionId` is set.
   */
  replacesBankConnectionId?: string;
  /**
   * Called once the linked accounts are persisted. `isReconnection` is true
   * for a reconnection and for a replacement, both of which leave the
   * household's existing accounts in place; `accounts` then holds only the
   * ones the session added.
   */
  onLinked?: (accounts: LinkedFinancialAccounts, context: { isReconnection: boolean }) => void;
  /**
   * Called when the user backs out without linking — dismissing the
   * connection modal without a selection, or exiting the Plaid flow. Not called
   * on the error path (an error re-surfaces the modal so the user can retry).
   */
  onCancelled?: () => void;
}

export interface IUseAccountLink {
  /** Start the flow — open the selection modal, or jump straight to Plaid when `bankConnectionId` was given. */
  trigger: () => void;
  /** True when the flow skips the selection modal (a reconnection target was supplied). */
  shouldSkipModal: boolean;
  isModalOpen: boolean;
  onModalOpenChange: (isOpen: boolean) => void;
  /** `intent` is what the session against the chosen connection is for; omitted, it is a reconnect. */
  onModalSelect: (bankConnectionId: string | undefined, intent?: LinkTokenIntent) => void;
  errorCode: ApiErrorCode | null;
  /** Why a replacement was refused, beside a `DUPLICATE_ITEM` error; null for every other error and no error. */
  replaceRefusal: PlaidLinkTokenReplaceRefusal | null;
  isLoading: boolean;
}

/**
 * The shared Plaid account-linking state machine: connection-selection modal →
 * link token → Plaid Link → wait for the session's banks (new connection) or
 * add-accounts (reconnection) → tracking. The single source of truth behind both the
 * onboarding/settings `PlaidLinkButton` and the assistant's
 * `financial_account_connect` flow, so duplicate handling, reconnection, and
 * analytics can't drift between the two entry points.
 *
 * A new-bank session is a multi-item one and hands nothing to `onSuccess`:
 * its banks land server-side off Plaid's webhook, which records what each
 * became. The hook waits on that record — the subscription for speed, the
 * query as the floor — and reports the new accounts, the existing duplicate
 * message, or an error; a session that linked some banks and failed others
 * reports both. `PLAID_LINK_PENDING` only when no record shows up in time and
 * the reads themselves were healthy.
 *
 * The hook owns the flow; the caller renders the `PlaidConnectionModal` from
 * the returned `{ isModalOpen, onModalOpenChange, onModalSelect, errorCode }`
 * and decides what triggers it (a button click vs. mounting). A modal
 * selection carries its own intent, which replaces `options.intent` for that
 * session: `AddInvestments` for a held connection that can take investment
 * accounts, none (a reconnect) otherwise. Outcomes surface through `onLinked` /
 * `onCancelled`; errors re-open the modal with a `DUPLICATE_ITEM`-aware message
 * rather than ending the flow.
 *
 * @param options.bankConnectionId Reconnection target. A string/`null` skips
 *   the selection modal and goes straight to Plaid for that connection;
 *   `undefined` (default) opens the modal first.
 * @param options.intent What the session against `bankConnectionId` is for;
 *   defaults to a reconnect.
 * @param options.replacesBankConnectionId A held connection a fresh link to
 *   its institution replaces. Skips the modal like a reconnection and opens a
 *   new-bank session whose `REPLACED` outcome is reported as a reconnection;
 *   a refused replacement reports `DUPLICATE_ITEM` with `replaceRefusal`
 *   beside it. Ignored when `bankConnectionId` is set.
 * @param options.onLinked Called once the linked accounts are persisted, with
 *   the accounts and `{ isReconnection }`. Passed through a ref, so an inline
 *   arrow is fine.
 * @param options.onCancelled Called when the user backs out without linking
 *   (modal dismissed without a selection, or Plaid exited). Not called on the
 *   error path. Also ref-stable.
 * @returns The flow controls: `trigger` to start it, the
 *   `PlaidConnectionModal` props (`isModalOpen` / `onModalOpenChange` /
 *   `onModalSelect` / `errorCode`), `shouldSkipModal` (true when a reconnection
 *   target was supplied, so the caller surfaces errors inline instead of in the
 *   modal), and `isLoading` for a trigger control.
 */
export function useAccountLink({
  bankConnectionId,
  intent,
  replacesBankConnectionId,
  onLinked,
  onCancelled,
}: IUseAccountLinkOptions = {}): IUseAccountLink {
  const { financialAccounts, refetch: refetchFinancialAccounts } = useFinancialAccounts();
  const client = useApolloClient();
  const subscriptionsEnabled = useFeatureGate(FeatureGate.GraphqlSubscriptions);
  const financialAccountsRef = React.useRef(financialAccounts);
  financialAccountsRef.current = financialAccounts;
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedBankConnectionId, setSelectedBankConnectionId] = React.useState<string | null | undefined>(
    bankConnectionId
  );
  const [selectedIntent, setSelectedIntent] = React.useState<LinkTokenIntent | undefined>(intent);
  const [shouldTriggerPlaid, setShouldTriggerPlaid] = React.useState(false);
  const [linkToken, setLinkToken] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  // One value, so a refusal can never outlive the error it explains.
  const [error, setError] = React.useState<{
    code: ApiErrorCode;
    replaceRefusal: PlaidLinkTokenReplaceRefusal | null;
  } | null>(null);
  const errorCode = error?.code ?? null;
  const replaceRefusal = error?.replaceRefusal ?? null;
  const clearError = React.useCallback(() => setError(null), []);

  // A named connection — one to reconnect or one to replace — skips the
  // selection modal and goes straight to Plaid.
  const shouldSkipModal = bankConnectionId !== undefined || replacesBankConnectionId !== undefined;

  // Read the outcome callbacks through refs so callers can pass inline arrows
  // without destabilizing the memoized handlers below (and so a callback can't
  // go stale across the async exchange).
  const onLinkedRef = React.useRef(onLinked);
  onLinkedRef.current = onLinked;
  const onCancelledRef = React.useRef(onCancelled);
  onCancelledRef.current = onCancelled;

  // Whether the open Link session is a new-bank one, which may connect several
  // banks and reports them over the webhook; a reconnection hands its accounts
  // to `onSuccess`. A ref, not state: Plaid captures the callbacks when the
  // handler is built, and this is read from inside them.
  const isMultiItemSessionRef = React.useRef(false);
  // The token the open session runs on. Its row is what the session's outcome
  // lands in, and the poll below reads it by this token.
  const linkTokenRef = React.useRef<string | null>(null);
  const isMountedRef = React.useRef(false);

  const reset = React.useCallback(() => {
    setLinkToken(null);
    setShouldTriggerPlaid(false);
    setSelectedBankConnectionId(undefined);
    setSelectedIntent(undefined);
    setIsLoading(false);
    isMultiItemSessionRef.current = false;
    linkTokenRef.current = null;
  }, []);

  const openModalWithError = React.useCallback(
    (code: ApiErrorCode, refusal: PlaidLinkTokenReplaceRefusal | null = null) => {
      setError({ code, replaceRefusal: refusal });
      // When skipping the modal there's nowhere to surface a modal error — the
      // caller renders an inline message off `errorCode` instead.
      if (!shouldSkipModal) {
        setIsModalOpen(true);
      }
      reset();
    },
    [reset, shouldSkipModal]
  );

  const [createLinkToken] = useMutation<CreateLinkTokenMutation, CreateLinkTokenMutationVariables>(CREATE_LINK_TOKEN, {
    errorPolicy: 'all',
  });
  const [createFinancialAccounts] = useMutation<
    CreateFinancialAccountsMutation,
    CreateFinancialAccountsMutationVariables
  >(CREATE_FINANCIAL_ACCOUNTS, { errorPolicy: 'all' });
  const [archiveBankConnectionWorkItems] = useMutation(ARCHIVE_BANK_CONNECTION_WORK_ITEMS, { errorPolicy: 'all' });

  const trackNewAccounts = React.useCallback(
    (accounts: LinkedFinancialAccounts, isReconnection: boolean) => {
      accounts.forEach((account) => {
        trackAccountConnected({
          accountId: account.id,
          accountType: account.type,
          accountSubtype: account.subtype ?? null,
          bankName: account.bank.name,
          bankId: account.bank.id,
          isFirstAccount: financialAccounts.length === 0,
          isReconnection,
        });
      });
      if (!isReconnection) {
        incrementPeopleProperty('account_count', accounts.length);
      }
    },
    [financialAccounts.length]
  );

  const onCreateLinkToken = React.useCallback(
    async (connectionId: string | null | undefined, connectionIntent: LinkTokenIntent | undefined) => {
      try {
        setIsLoading(true);
        clearError();
        // A replacement is a new-bank session that names the connection it
        // replaces; the server refuses the hint beside a connection or an intent.
        const replacing = connectionId === undefined && replacesBankConnectionId !== undefined;
        const response = await createLinkToken({
          variables: {
            input: replacing
              ? { replacesBankConnectionId }
              : {
                  bankConnectionId: connectionId ?? undefined,
                  // The server refuses an intent that names no connection.
                  intent: connectionId ? connectionIntent : undefined,
                },
          },
        });
        if (response.errors) {
          logger.error('Failed to create link token');
          openModalWithError(extractErrorCode(response.errors));
          return;
        }
        const token = response.data?.createLinkToken?.linkToken;
        if (!token) {
          logger.error('Failed to create link token');
          openModalWithError(ApiErrorCode.PLAID_LINK_TOKEN_FAILED);
          return;
        }
        isMultiItemSessionRef.current = !connectionId;
        linkTokenRef.current = token;
        setLinkToken(token);
      } catch (err) {
        logger.error('Error occurred while creating link token', err);
        openModalWithError(ApiErrorCode.INTERNAL_SERVER_ERROR);
      }
    },
    [createLinkToken, openModalWithError, clearError, replacesBankConnectionId]
  );

  const onAddAccounts = React.useCallback(
    async (metadata: PlaidLinkOnSuccessMetadata) => {
      try {
        setIsLoading(true);
        clearError();
        const institutionId = metadata.institution?.institution_id;
        if (!institutionId) {
          logger.error('Missing institution information');
          openModalWithError(ApiErrorCode.INTERNAL_SERVER_ERROR);
          return;
        }
        const accounts = metadata.accounts.map((account) => ({
          id: account.id,
          name: account.name,
          mask: account.mask,
          type: account.type,
          subtype: account.subtype,
        }));

        // Link ran against this connection's token, so name it: the server's
        // institution lookup cannot tell two connections at one bank apart.
        const response = await createFinancialAccounts({
          variables: { input: { institutionId, accounts, bankConnectionId: selectedBankConnectionId ?? undefined } },
        });
        if (response.errors) {
          logger.error('Failed to create financial accounts');
          openModalWithError(extractErrorCode(response.errors));
          return;
        }
        if (!response.data?.createFinancialAccounts) {
          logger.error('Failed to create financial accounts');
          openModalWithError(ApiErrorCode.INTERNAL_SERVER_ERROR);
          return;
        }
        trackNewAccounts(response.data.createFinancialAccounts, true);
        if (selectedBankConnectionId) {
          try {
            await archiveBankConnectionWorkItems({ variables: { bankConnectionId: selectedBankConnectionId } });
          } catch (err) {
            // Don't fail the reconnection if the archive fails.
            logger.error('Failed to archive bank connection work items', err);
          }
        }
        onLinkedRef.current?.(response.data.createFinancialAccounts, { isReconnection: true });
      } catch (err) {
        logger.error('Error occurred while adding accounts', err);
        openModalWithError(ApiErrorCode.INTERNAL_SERVER_ERROR);
      } finally {
        reset();
      }
    },
    [
      selectedBankConnectionId,
      createFinancialAccounts,
      archiveBankConnectionWorkItems,
      openModalWithError,
      reset,
      trackNewAccounts,
      clearError,
    ]
  );

  /**
   * Waits for the server's record of what a session became. The subscription
   * carries it the moment the webhook is handled and is matched on Plaid's
   * session id; the query underneath reads the row by our link token and is
   * the floor, since delivery is at-most-once. Resolves null when nothing
   * terminal arrives in time or the hook has unmounted.
   */
  const waitForLinkSession = React.useCallback(
    (linkToken: string, linkSessionId: string | null, timeoutMs: number): Promise<ILinkSessionWait> =>
      new Promise((resolve) => {
        let settled = false;
        let lastPollFailed = false;
        const isTerminal = (session: PlaidLinkTokenFieldsFragment | null | undefined) =>
          session?.status === PlaidLinkTokenStatus.Completed || session?.status === PlaidLinkTokenStatus.Failed;
        const finish = (session: PlaidLinkTokenFieldsFragment | null) => {
          if (settled) {
            return;
          }
          settled = true;
          subscription?.unsubscribe();
          resolve({ session, pollFailed: session === null && lastPollFailed });
        };

        const subscription = subscriptionsEnabled
          ? client.subscribe<PlaidLinkTokenUpdatedSubscription>({ query: PLAID_LINK_TOKEN_UPDATED }).subscribe({
              next: ({ data }) => {
                const session = data?.plaidLinkTokenUpdated;
                if (
                  session &&
                  linkSessionId !== null &&
                  session.linkSessionId === linkSessionId &&
                  isTerminal(session)
                ) {
                  finish(session);
                }
              },
              error: (err: unknown) => {
                logger.warn(`Link session subscription error: ${String(err)}`);
              },
            })
          : null;

        const deadline = Date.now() + timeoutMs;
        void (async () => {
          while (!settled && Date.now() < deadline && isMountedRef.current) {
            await sleep(LINK_SESSION_POLL_MS);
            if (settled) {
              return;
            }
            try {
              const { data } = await client.query<PlaidLinkTokenQuery, PlaidLinkTokenQueryVariables>({
                query: PLAID_LINK_TOKEN,
                variables: { linkToken },
                fetchPolicy: 'network-only',
              });
              lastPollFailed = false;
              const session = data.plaidLinkToken ?? null;
              if (session && isTerminal(session)) {
                finish(session);
                return;
              }
            } catch (err) {
              lastPollFailed = true;
              logger.warn(`Link session poll failed: ${String(err)}`);
            }
          }
          finish(null);
        })();
      }),
    [client, subscriptionsEnabled]
  );

  /**
   * Resolves a multi-item session from its recorded outcome. `onNothing`
   * decides what no record means: still pending after a completed session, or
   * a plain cancel after an exit. A record with nothing linked and nothing
   * wrong — the user backed out before any bank — is a cancel either way.
   *
   * A session that linked some banks and failed others keeps both halves: the
   * accounts that arrived are handed to `onLinked`, and the failure still
   * surfaces as `PLAID_EXCHANGE_FAILED`.
   */
  const awaitLinkSession = React.useCallback(
    async (linkSessionId: string | null, timeoutMs: number, onNothing: 'pending' | 'canceled') => {
      setIsLoading(true);
      clearError();
      const known = new Set(financialAccountsRef.current.map((account) => account.id));
      const linkToken = linkTokenRef.current;
      const { session, pollFailed } = linkToken
        ? await waitForLinkSession(linkToken, linkSessionId, timeoutMs)
        : { session: null, pollFailed: false };
      if (!isMountedRef.current) {
        return;
      }

      const outcomes = session?.results.map((result) => result.outcome) ?? [];
      // A spent token is a server-side exchange failure, not a bank the user declined.
      const hasFailure =
        session?.status === PlaidLinkTokenStatus.Failed ||
        outcomes.includes(PlaidLinkTokenItemOutcome.Failed) ||
        outcomes.includes(PlaidLinkTokenItemOutcome.Spent);

      // A replacement moves the household's accounts onto the new Item under
      // their own ids, so it reads as a reconnection, and the work items that
      // followed the accounts across need re-reading too. What arrived is
      // whatever any Item in the session added: the accounts the replacing
      // Item carried that the old one did not, and every account of any other
      // bank that linked beside it. A refused replacement is recorded as the
      // duplicate it would have been, with the reason beside it.
      const replaced = outcomes.includes(PlaidLinkTokenItemOutcome.Replaced);
      const linked = outcomes.includes(PlaidLinkTokenItemOutcome.Linked);
      const refusal = session?.results.find((result) => result.replaceRefusal != null)?.replaceRefusal ?? null;
      if (linked || replaced) {
        let arrived: LinkedFinancialAccounts = [];
        try {
          const { data } = await refetchFinancialAccounts();
          arrived = (data?.getFinancialAccountsForUser ?? []).filter((account) => !known.has(account.id));
          evictHouseholdNetWorth(client.cache);
        } catch (err) {
          // The banks did link; we just cannot read them back, so the caller
          // must not be handed an empty list as a success.
          logger.error('Failed to read the accounts a Link session added', err);
          openModalWithError(ApiErrorCode.INTERNAL_SERVER_ERROR);
          return;
        }
        // The webhook path never runs the createFinancialAccounts cache write,
        // so the connection list would keep serving the pre-link banks.
        evictBankConnectionReads(client.cache);
        if (replaced) {
          evictWorkItemReads(client.cache);
        }
        // Whatever arrived is new to the household, whichever Item added it:
        // a replacement's kept accounts keep their ids and are not in it.
        trackNewAccounts(arrived, false);
        if (hasFailure) {
          onLinkedRef.current?.(arrived, { isReconnection: replaced });
          openModalWithError(ApiErrorCode.PLAID_EXCHANGE_FAILED);
          return;
        }
        if (!replaced && refusal !== null) {
          // Some other bank linked while the one this session was opened to
          // replace was refused: both halves are reported.
          onLinkedRef.current?.(arrived, { isReconnection: false });
          openModalWithError(ApiErrorCode.DUPLICATE_ITEM, refusal);
          return;
        }
        reset();
        onLinkedRef.current?.(arrived, { isReconnection: replaced });
        return;
      }
      if (hasFailure) {
        openModalWithError(ApiErrorCode.PLAID_EXCHANGE_FAILED);
        return;
      }
      if (outcomes.length > 0 && outcomes.every((outcome) => outcome === PlaidLinkTokenItemOutcome.Duplicate)) {
        openModalWithError(ApiErrorCode.DUPLICATE_ITEM, refusal);
        return;
      }
      if (pollFailed) {
        // Reads kept failing, so the session may well have a terminal record —
        // reporting it as pending or canceled would be a guess.
        openModalWithError(ApiErrorCode.INTERNAL_SERVER_ERROR);
        return;
      }
      if (session === null && onNothing === 'pending') {
        openModalWithError(ApiErrorCode.PLAID_LINK_PENDING);
        return;
      }
      reset();
      onCancelledRef.current?.();
    },
    [waitForLinkSession, refetchFinancialAccounts, client, openModalWithError, reset, trackNewAccounts, clearError]
  );

  const config: PlaidLinkOptions = {
    token: linkToken,
    onSuccess: (_publicToken, metadata) => {
      if (isMultiItemSessionRef.current) {
        void awaitLinkSession(metadata.link_session_id ?? null, LINK_SESSION_WAIT_MS, 'pending');
        return;
      }
      onAddAccounts(metadata).catch(() => {
        // Error handling is done in onAddAccounts.
      });
    },
    onExit: (err, metadata) => {
      trackPlaidLinkExited({ hadError: !!err });
      if (err) {
        logger.error('Link was exited with an error', err);
        openModalWithError(ApiErrorCode.INTERNAL_SERVER_ERROR);
        return;
      }
      if (isMultiItemSessionRef.current) {
        void awaitLinkSession(metadata?.link_session_id ?? null, LINK_SESSION_EXIT_WAIT_MS, 'canceled');
        return;
      }
      // User backed out of Plaid without an error and without linking.
      reset();
      onCancelledRef.current?.();
    },
  };

  const { open, ready, exit } = usePlaidLink(config);

  // Auto-open Plaid once a link token is ready.
  React.useEffect(() => {
    if (linkToken && ready) {
      trackPlaidLinkOpened();
      open();
    }
  }, [linkToken, ready, open]);

  // Kick off token creation when a selection (or a skip-modal trigger) arms the flow.
  React.useEffect(() => {
    if (shouldTriggerPlaid && !linkToken && !isLoading) {
      onCreateLinkToken(selectedBankConnectionId, selectedIntent).catch(() => {
        // Error handling is done in onCreateLinkToken.
      });
    }
  }, [shouldTriggerPlaid, linkToken, isLoading, selectedBankConnectionId, selectedIntent, onCreateLinkToken]);

  // Close any open Plaid modal if the flow unmounts mid-link (e.g. the assistant
  // turn was cancelled). `exit` identity changes across renders, so call the
  // latest via a ref in a cleanup that runs exactly once on unmount.
  const exitRef = React.useRef(exit);
  exitRef.current = exit;
  // Set on every mount, not just initialized: StrictMode runs this cleanup
  // once on mount in dev, and a flag that only ever goes false would leave the
  // wait below returning before its first poll.
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      exitRef.current();
    };
  }, []);

  const trigger = React.useCallback(() => {
    if (shouldSkipModal) {
      // Restore the reconnection target (a prior error/exit may have reset it).
      setSelectedBankConnectionId(bankConnectionId);
      setSelectedIntent(intent);
      setShouldTriggerPlaid(true);
    } else {
      setIsModalOpen(true);
    }
  }, [shouldSkipModal, bankConnectionId, intent]);

  // Distinguishes a modal close caused by a selection from a real user
  // dismissal — both arrive as onOpenChange(false). onModalSelect sets this
  // flag; onModalOpenChange reads it on the NEXT microtask, so it's correct
  // regardless of whether the modal fires onSelect before or after
  // onOpenChange (both run in the same synchronous tick, before the microtask).
  // Only a genuine dismissal leaves the flag false → onCancelled.
  const advancingRef = React.useRef(false);

  const onModalSelect = React.useCallback((connectionId: string | undefined, connectionIntent?: LinkTokenIntent) => {
    advancingRef.current = true;
    setSelectedBankConnectionId(connectionId);
    // Set even when absent, so a plain pick never inherits an earlier pick's intent.
    setSelectedIntent(connectionIntent);
    setShouldTriggerPlaid(true);
  }, []);

  const onModalOpenChange = React.useCallback(
    (nextIsOpen: boolean) => {
      setIsModalOpen(nextIsOpen);
      if (!nextIsOpen) {
        clearError();
        queueMicrotask(() => {
          if (advancingRef.current) {
            advancingRef.current = false;
            return;
          }
          onCancelledRef.current?.();
        });
      }
    },
    [clearError]
  );

  return {
    trigger,
    shouldSkipModal,
    isModalOpen,
    onModalOpenChange,
    onModalSelect,
    errorCode,
    replaceRefusal,
    isLoading,
  };
}
